import React, { useState, useEffect } from 'react';
import LoadingSpinner from "./LoadingSpinner";
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { FirebaseService } from '../services/firebaseService';
// Removed Header import since it's provided by App.tsx

interface CategoryProductsProps {
  t: any;
  isRTL: boolean;
  language: 'fr' | 'en' | 'ar';
  setLanguage: (lang: 'fr' | 'en' | 'ar') => void;
}

interface Product {
  id: string;
  name: string;
  mainImage: string;
  description: string[];
  category: string;
  provider: string;
  technicalSpecs: any;
  additionalImages?: string[];
  createdAt?: any;
  updatedAt?: any;
}

const CategoryProductsPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #ffffff;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  position: relative;
  overflow-x: hidden;
`;

const Container = styled.div`
  max-width: 1800px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 1;
`;

const HeroSection = styled.div`
  padding: 6rem 0 4rem;
  text-align: center;
  position: relative;
`;

const PageTitle = styled(motion.h1)<{ $isRTL: boolean }>`
  font-size: 2.5rem;
  color: #6b7280;
  margin-bottom: 1.5rem;
  position: relative;
  font-weight: 400;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 6px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 3px;
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// eslint-disable-next-line @typescript-eslint/no-unused-vars

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PageSubtitle = styled(motion.p)<{ $isRTL: boolean }>`
  font-size: 1.4rem;
  color: #6b7280;
  max-width: 600px;
  margin: 0 auto 3rem;
  line-height: 1.6;
  font-weight: 400;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const ProductCard = styled(motion.div)`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #dc2626, #b91c1c);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    border-color: #dc2626;
    box-shadow: 0 20px 40px rgba(220, 38, 38, 0.15);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const ProductImage = styled.div`
  height: 300px;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 2rem;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.4s ease;
    border-radius: 12px;
  }
  
  &:hover img {
    transform: scale(1.1);
  }
`;

const ProductContent = styled.div`
  padding: 2rem;
`;

const ProductTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.8rem;
  line-height: 1.4;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ProductDescription = styled.p<{ $isRTL: boolean }>`
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ProductButton = styled.button<{ $isRTL: boolean }>`
  width: 100%;
  padding: 1.2rem;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white;
  border: none;
  border-radius: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  font-size: 1rem;
  position: relative;
  overflow: hidden;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(220, 38, 38, 0.4);
    
    &::before {
      left: 100%;
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 500px;
  gap: 2rem;
`;


// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LoadingText = styled.div<{ $isRTL: boolean }>`
  font-size: 1.2rem;
  color: #6b7280;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 6rem 2rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const EmptyIcon = styled.div`
  width: 150px;
  height: 150px;
  margin: 0 auto 2rem;
  opacity: 0.6;
  
  img {
    width: 100%;
    height: 100%;
    filter: brightness(0) saturate(100%) invert(60%) sepia(8%) saturate(750%) hue-rotate(185deg) brightness(95%) contrast(89%);
  }
`;

const EmptyTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #1f2937;
  font-weight: 700;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const EmptyText = styled.p<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #6b7280;
  line-height: 1.6;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const CategoryProducts: React.FC<CategoryProductsProps> = ({ t, isRTL, language, setLanguage }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
    if (categoryId) {
      loadProducts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const allProducts = await FirebaseService.getAll('products');
      const categoryProducts = allProducts?.filter((product: Product) => 
        product.category === categoryId
      ) || [];
      setProducts(categoryProducts);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (productId: string) => {
    navigate(`/product/${productId}`);
  };

  const getCategoryName = (categoryId: string) => {
    const categoryMap: { [key: string]: string } = {
      '0': 'Materiel & Equipment de Distribution Electrique',
      '1': 'Groupes Electrogènes',
      '2': 'Accessoires Electriques'
    };
    return categoryMap[categoryId] || 'Category';
  };

  if (loading) {
    return (
      <CategoryProductsPage $isRTL={isRTL}>
        {/* Removed Header component */}
        <Container>
          <LoadingSpinner />
        </Container>
      </CategoryProductsPage>
    );
  }

  return (
    <CategoryProductsPage $isRTL={isRTL}>
      {/* Removed Header component */}

      <Container>
        <HeroSection>
          <PageTitle
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {getCategoryName(categoryId || '')}
          </PageTitle>
        </HeroSection>

        {products.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <img src="/images/allpro/empty.svg" alt="No products found" />
            </EmptyIcon>
            <EmptyTitle $isRTL={isRTL}>
              {t.noProductsFound || 'No products found'}
            </EmptyTitle>
            <EmptyText $isRTL={isRTL}>
              {t.noProductsFoundText || 'No products available in this category'}
            </EmptyText>
          </EmptyState>
        ) : (
          <ProductsGrid>
            {products.map((product, index) => (
              <ProductCard
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => handleProductClick(product.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ProductImage>
                  <img
                    src={product.mainImage || '/images/products/default-product.jpg'}
                    alt={product.name}
                    onError={(e) => {
                      e.currentTarget.src = '/images/products/default-product.jpg';
                    }}
                  />
                </ProductImage>
                <ProductContent>
                  <ProductTitle $isRTL={isRTL}>
                    {product.name}
                  </ProductTitle>
                  <ProductDescription $isRTL={isRTL}>
                    {Array.isArray(product.description) 
                      ? product.description.join(' ') 
                      : product.description
                    }
                  </ProductDescription>
                  <ProductButton $isRTL={isRTL}>
                    {t.viewDetails || 'View Details'}
                  </ProductButton>
                </ProductContent>
              </ProductCard>
            ))}
          </ProductsGrid>
        )}
      </Container>
    </CategoryProductsPage>
  );
};

export default CategoryProducts;